import { ArrowRight, Calendar, MessageSquare, Users, Mail, ChevronDown } from "lucide-react"
import CommunityBanner from "./CommunityBanner"

export function CommunityHomepage({
}) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommunityBanner />

      {/* Hero Section */}
      <section className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-600" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Welcome to Tech Innovators</h1>
          <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            A community of passionate developers, designers, and tech enthusiasts building the future together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors">
              Join Our Community
            </button>
            <button className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-400 transition-colors">
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">What We Offer</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Everything you need to grow and succeed in tech
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Expert Workshops",
                description: "Learn from industry leaders in live workshops and webinars",
                icon: Users,
              },
              {
                title: "Resource Library",
                description: "Access our curated collection of tutorials and guides",
                icon: MessageSquare,
              },
              {
                title: "Mentorship",
                description: "Connect with experienced mentors in your field",
                icon: Mail,
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="relative group bg-gray-50 dark:bg-gray-700 p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-gray-50 dark:bg-gray-700 p-8 rounded-xl">
                  <feature.icon className="w-12 h-12 text-indigo-600 dark:text-indigo-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold">Upcoming Events</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">Join our latest workshops and meetups</p>
            </div>
            <button className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
              View All Events
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((event) => (
              <div
                key={event}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-video relative">
                  <img
                    src="/placeholder.svg?height=200&width=400"
                    alt="Event cover"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-indigo-600 text-white text-sm rounded-full">
                    Workshop
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <Calendar className="w-4 h-4" />
                    June 15, 2024 • 2:00 PM EST
                  </div>
                  <h3 className="text-xl font-bold mb-2">Advanced React Patterns</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Learn advanced React patterns and best practices from industry experts.
                  </p>
                  <button className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                    Register Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Latest Resources</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Explore our latest tutorials and guides</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((resource) => (
              <div
                key={resource}
                className="flex gap-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:shadow-lg transition-shadow"
              >
                <img
                  src="/placeholder.svg?height=100&width=100"
                  alt="Resource thumbnail"
                  className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                />
                <div>
                  <span className="text-sm text-indigo-600 dark:text-indigo-400">Tutorial</span>
                  <h3 className="text-lg font-bold mt-1 mb-2">Building Scalable Applications</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
                    Learn how to build and deploy scalable applications using modern technologies.
                  </p>
                  <button className="mt-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
                    Read More →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Find answers to common questions</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "How can I join the community?",
                answer: "You can join by clicking the 'Join Community' button and following the registration process.",
              },
              {
                question: "What benefits do members get?",
                answer: "Members get access to exclusive workshops, resources, mentorship opportunities, and more.",
              },
              {
                question: "How can I contribute?",
                answer: "You can contribute by sharing knowledge, participating in discussions, or becoming a mentor.",
              },
            ].map((faq, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6">
                <button className="flex items-center justify-between w-full text-left">
                  <span className="font-semibold">{faq.question}</span>
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </button>
                <p className="mt-4 text-gray-600 dark:text-gray-300">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-8">Ready to Join Our Community?</h2>
          <button className="px-8 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors">
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
}

